import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select';
import { RefreshCw, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import gql from 'graphql-tag';
import type { ReportFilter, ReportFilterOption } from './reports.gql';
import type { FilterValidationErrors } from './report-utils';

interface ReportFiltersBarProps {
	filters: ReportFilter[];
	values: Record<string, unknown>;
	onChange: (key: string, value: unknown) => void;
	onRun: () => void;
	isRunning: boolean;
	errors?: FilterValidationErrors;
	searchText?: string;
	onSearchChange?: (value: string) => void;
	searchPlaceholder?: string;
}

export function ReportFiltersBar({
	filters,
	values,
	onChange,
	onRun,
	isRunning,
	errors = {},
	searchText,
	onSearchChange,
	searchPlaceholder
}: ReportFiltersBarProps) {
	const sortedFilters = useMemo(
		() => [...filters].sort((a, b) => a.sortOrder - b.sortOrder),
		[filters]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				onRun();
			}
		},
		[onRun]
	);

	return (
		<div
			className="flex flex-wrap items-end gap-4"
			onKeyDown={handleKeyDown}
		>
			{sortedFilters.map(filter => (
				<FilterControl
					key={filter.reportFilterId}
					filter={filter}
					value={values[filter.key]}
					onChange={val => onChange(filter.key, val)}
					error={errors[filter.key]}
					parentFilterValue={
						filter.dependsOnFilterKey
							? values[filter.dependsOnFilterKey]
							: undefined
					}
				/>
			))}

			<Button
				onClick={onRun}
				disabled={isRunning}
				size="default"
				className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
			>
				<RefreshCw
					className={`h-4 w-4 ${isRunning ? 'animate-spin' : ''}`}
				/>
				Run
			</Button>

			{onSearchChange && (
				<div className="relative ml-auto">
					<Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
					<Input
						id="report-search"
						name="report-search"
						value={searchText ?? ''}
						onChange={e => onSearchChange(e.target.value)}
						placeholder={searchPlaceholder}
						className="h-8 w-52 pl-8 text-sm"
					/>
				</div>
			)}
		</div>
	);
}

const VALIDATION_MESSAGES: Record<string, string> = {
	required: 'validation.required',
	invalidDate: 'validation.invalidDate'
};

interface FilterControlProps {
	filter: ReportFilter;
	value: unknown;
	onChange: (value: unknown) => void;
	error?: string;
	parentFilterValue?: unknown;
}

/**
 * Parses OptionsSourceJson to extract GraphQL query and variable mapping.
 * Expected format: { "query": "...", "variableMapping": { "parentKey": "$parentFilterKey" } }
 */
interface OptionsSource {
	query: string;
	variableMapping?: Record<string, string>;
	valuePath?: string;
	labelPath?: string;
}

function parseOptionsSource(json: string | null): OptionsSource | null {
	if (!json) return null;
	try {
		const parsed = JSON.parse(json);
		if (typeof parsed?.query !== 'string') return null;
		return parsed as OptionsSource;
	} catch {
		return null;
	}
}

function FilterControl({ filter, value, onChange, error, parentFilterValue }: FilterControlProps) {
	const { t } = useTranslation('reports');
	const client = useApolloClient();
	const errorMessage = error ? t(VALIDATION_MESSAGES[error] ?? error) : null;
	const errorBorder = error ? 'border-destructive' : '';
	const fieldId = `report-filter-${filter.key}`;

	// Dynamic options state for cascading filters
	const [dynamicOptions, setDynamicOptions] = useState<ReportFilterOption[] | null>(null);
	const optionsSource = useMemo(() => parseOptionsSource(filter.optionsSourceJson), [filter.optionsSourceJson]);

	// Reload options when parent filter value changes (cascading)
	useEffect(() => {
		if (!optionsSource || !filter.dependsOnFilterKey) return;
		if (parentFilterValue == null) {
			setDynamicOptions(null);
			return;
		}

		let cancelled = false;

		(async () => {
			try {
				const variables: Record<string, unknown> = {};
				if (optionsSource.variableMapping) {
					for (const [varName, mapping] of Object.entries(optionsSource.variableMapping)) {
						if (typeof mapping === 'string' && mapping.startsWith('$')) {
							variables[varName] = parentFilterValue;
						} else {
							variables[varName] = mapping;
						}
					}
				}

				const { data } = await client.query({
					query: gql`${optionsSource.query}`,
					variables,
					fetchPolicy: 'network-only'
				});

				if (cancelled) return;

				// Extract options from response using valuePath/labelPath or auto-detect
				const valuePath = optionsSource.valuePath || 'value';
				const labelPath = optionsSource.labelPath || 'label';

				// Find the first array in the response data
				const findArray = (obj: unknown): unknown[] | null => {
					if (Array.isArray(obj)) return obj;
					if (obj != null && typeof obj === 'object') {
						for (const v of Object.values(obj as Record<string, unknown>)) {
							const found = findArray(v);
							if (found) return found;
						}
					}
					return null;
				};

				const arr = findArray(data);
				if (arr) {
					const opts: ReportFilterOption[] = arr.map((item: unknown) => {
						const rec = item as Record<string, unknown>;
						return {
							value: String(rec[valuePath] ?? ''),
							label: String(rec[labelPath] ?? rec[valuePath] ?? '')
						};
					});
					setDynamicOptions(opts);
				}
			} catch (err) {
				console.error('Failed to load cascading filter options:', err);
				setDynamicOptions(null);
			}
		})();

		return () => { cancelled = true; };
	}, [client, optionsSource, filter.dependsOnFilterKey, parentFilterValue]);

	// Use dynamic options if available, otherwise fall back to static options
	const effectiveOptions = dynamicOptions ?? filter.options;

	switch (filter.type) {
		case 'date':
			return (
				<div className="flex flex-col gap-1.5">
					<Label htmlFor={fieldId} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						{filter.label}{filter.required && <span className="text-destructive ml-0.5">*</span>}
					</Label>
					<Input
						id={fieldId}
						name={filter.key}
						type="date"
						value={(value as string) ?? ''}
						onChange={e => onChange(e.target.value || undefined)}
						className={`h-9 w-44 text-sm ${errorBorder}`}
					/>
					{errorMessage && <span className="text-xs text-destructive">{errorMessage}</span>}
				</div>
			);

			case 'select':
			return (
				<div className="flex flex-col gap-1.5">
					<Label htmlFor={fieldId} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						{filter.label}{filter.required && <span className="text-destructive ml-0.5">*</span>}
					</Label>
					<Select
						value={(value as string) ?? ''}
						name={filter.key}
						onValueChange={val =>
							onChange(val === '__all__' ? undefined : val)
						}
					>
						<SelectTrigger id={fieldId} className={`h-9 w-44 text-sm ${errorBorder}`}>
							<SelectValue placeholder="All" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="__all__">All</SelectItem>
							{effectiveOptions.map(opt => (
								<SelectItem key={opt.value} value={opt.value}>
									{opt.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{errorMessage && <span className="text-xs text-destructive">{errorMessage}</span>}
				</div>
			);

			case 'text':
			return (
				<div className="flex flex-col gap-1.5">
					<Label htmlFor={fieldId} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						{filter.label}{filter.required && <span className="text-destructive ml-0.5">*</span>}
					</Label>
					<Input
						id={fieldId}
						name={filter.key}
						type="text"
						value={(value as string) ?? ''}
						onChange={e => onChange(e.target.value || undefined)}
						placeholder={filter.label}
						className={`h-9 w-44 text-sm ${errorBorder}`}
					/>
					{errorMessage && <span className="text-xs text-destructive">{errorMessage}</span>}
				</div>
			);

		case 'lookup':
			return (
				<div className="flex flex-col gap-1.5">
					<Label htmlFor={fieldId} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						{filter.label}{filter.required && <span className="text-destructive ml-0.5">*</span>}
					</Label>
					<Select
						value={(value as string) ?? ''}
						name={filter.key}
						onValueChange={val =>
							onChange(val === '__all__' ? undefined : val)
						}
					>
						<SelectTrigger id={fieldId} className={`h-9 w-44 text-sm ${errorBorder}`}>
							<SelectValue placeholder="All" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="__all__">All</SelectItem>
							{effectiveOptions.map(opt => (
								<SelectItem key={opt.value} value={opt.value}>
									{opt.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{errorMessage && <span className="text-xs text-destructive">{errorMessage}</span>}
				</div>
			);

		default:
			return (
				<div className="flex flex-col gap-1.5">
					<Label htmlFor={fieldId} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						{filter.label}{filter.required && <span className="text-destructive ml-0.5">*</span>}
					</Label>
					<Input
						id={fieldId}
						name={filter.key}
						type="text"
						value={(value as string) ?? ''}
						onChange={e => onChange(e.target.value || undefined)}
						className={`h-9 w-44 text-sm ${errorBorder}`}
					/>
					{errorMessage && <span className="text-xs text-destructive">{errorMessage}</span>}
				</div>
			);
	}
}
